import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './shadcn/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './shadcn/card';
import { Input } from './shadcn/input';
import { Label } from './shadcn/label';
import { Alert, AlertDescription } from './shadcn/alert';
import { UserPlus, AlertCircle, Eye } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { useValidation, validationRules } from '../utils/useValidation';
import { sanitizeInput, isValidSecureEmail, InputRateLimiter, validateFormSecurity } from '../utils/securityValidation';
import { logFailedLogin, logSuccessfulLogin, logRateLimitExceeded } from '../utils/securityLogging';
import { authAPI } from '../lib/api'; // Import authAPI for direct registration

function Register() {
  const { values, errors, touched, handleChange, handleBlur, validateForm } = useValidation(
    { companyName: '', email: '', password: '', confirmPassword: '' },
    validationRules.register
  );
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [securityError, setSecurityError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();
  const registerAttempts = useRef(new InputRateLimiter(3, 60 * 60 * 1000)); // 3 attempts per hour

  const handleMouseDownPassword = () => {
    setShowPassword(true);
  };

  const handleMouseUpPassword = () => {
    setShowPassword(false);
  };

  const handleMouseLeavePassword = () => {
    setShowPassword(false);
  };

  const handleMouseDownConfirmPassword = () => {
    setShowConfirmPassword(true);
  };

  const handleMouseUpConfirmPassword = () => {
    setShowConfirmPassword(false);
  };

  const handleMouseLeaveConfirmPassword = () => {
    setShowConfirmPassword(false);
  };

  // Check for excessive registration attempts
  useEffect(() => {
    const isAllowed = registerAttempts.current.isAllowed('register');
    if (!isAllowed) {
      setError('Too many registration attempts. Please try again later.');
      logRateLimitExceeded('/register', 3);
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Security check: Rate limiting
    const isAllowed = registerAttempts.current.isAllowed('register');
    if (!isAllowed) {
      setError('Too many registration attempts. Please try again later.');
      logRateLimitExceeded('/register', 3);
      return;
    }
    
    // Sanitize inputs
    const sanitizedValues = {
      companyName: sanitizeInput(values.companyName),
      email: sanitizeInput(values.email),
      password: sanitizeInput(values.password),
      confirmPassword: sanitizeInput(values.confirmPassword)
    };
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Additional security validation
    const securityCheck = validateFormSecurity(sanitizedValues);
    if (!securityCheck.isValid) {
      setSecurityError('Invalid input detected. Please check your entries.');
      logFailedLogin(sanitizedValues.email, 'invalid_input');
      return;
    }
    
    // Additional email validation
    if (!isValidSecureEmail(sanitizedValues.email)) {
      setSecurityError('Invalid email format detected.');
      logFailedLogin(sanitizedValues.email, 'invalid_email_format');
      return;
    }
    
    try {
      setError('');
      setSecurityError('');
      setLoading(true);
      
      // Register the user directly (no CSRF protection needed for registration)
      const response = await authAPI.register({
        companyName: sanitizedValues.companyName,
        email: sanitizedValues.email,
        password: sanitizedValues.password
      });

      if (response.data) {
        // Show success message and redirect to verification email sent page
        // This provides a better UX by informing the user what to do next
        setTimeout(() => {
          navigate('/verification-email-sent');
        }, 100); // Small delay to allow error state to clear
      } else {
        setError('Failed to create an account. Please try again.');
        logFailedLogin(sanitizedValues.email, 'registration_failed');
      }
    } catch (err) {
      setError('Failed to create an account. Please try again.');
      logFailedLogin(sanitizedValues.email, 'registration_exception');
    }
    
    setLoading(false);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <style>{`
        input::-ms-reveal,
        input::-ms-clear {
          display: none;
        }
        input::-webkit-credentials-auto-fill-button {
          display: none !important;
        }
        input::-webkit-caps-lock-indicator {
          display: none !important;
        }
        input::-webkit-textfield-decoration-container {
          display: none;
        }
      `}</style>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-primary flex items-center justify-center">
            <UserPlus className="h-8 w-8 text-white" />
          </div>
          <h1 className="mt-6 text-3xl font-extrabold text-gray-900">Create Account</h1>
          <p className="mt-2 text-gray-600">Get started with our video analysis platform</p>
        </div>
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl">Register</CardTitle>
            <CardDescription>Create your account to get started</CardDescription>
          </CardHeader>
          
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {securityError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{securityError}</AlertDescription>
              </Alert>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder="Enter your company name"
                  value={values.companyName}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                  onBlur={() => handleBlur('companyName')}
                  required
                  className={touched.companyName && errors.companyName ? 'border-red-500' : ''}
                />
                {touched.companyName && errors.companyName && (
                  <p className="text-sm text-red-500">{errors.companyName}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={values.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  required
                  className={touched.email && errors.email ? 'border-red-500' : ''}
                />
                {touched.email && errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={values.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    onBlur={() => handleBlur('password')}
                    required
                    autoComplete="new-password"
                    inputMode="text"
                    className={touched.password && errors.password ? 'border-red-500' : ''}
                  />
                  <button
                    type="button"
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    onMouseLeave={handleMouseLeavePassword}
                    onTouchStart={handleMouseDownPassword}
                    onTouchEnd={handleMouseUpPassword}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                </div>
                {touched.password && errors.password && (
                  <p className="text-sm text-red-500">{errors.password}</p>
                )}
                <p className="text-sm text-gray-500">Must contain uppercase, lowercase, number, and be at least 12 characters</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={values.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    onBlur={() => handleBlur('confirmPassword')}
                    required
                    autoComplete="new-password"
                    inputMode="text"
                    className={touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : ''}
                  />
                  <button
                    type="button"
                    onMouseDown={handleMouseDownConfirmPassword}
                    onMouseUp={handleMouseUpConfirmPassword}
                    onMouseLeave={handleMouseLeaveConfirmPassword}
                    onTouchStart={handleMouseDownConfirmPassword}
                    onTouchEnd={handleMouseUpConfirmPassword}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                </div>
                {touched.confirmPassword && errors.confirmPassword && (
                  <p className="text-sm text-red-500">{errors.confirmPassword}</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={loading || !!error}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>

              {loading && (
                <div className="text-sm text-gray-600 mt-2 text-center">
                  <p>Please check your email after registration to verify your account.</p>
                </div>
              )}
            </form>
          </CardContent>
          
          <CardFooter className="flex flex-col">
            <div className="text-sm text-center text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary hover:underline">
                Sign In
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}

export default Register;